import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Star, ChevronDown, ChevronUp, Mail, User, Phone, MessageSquare, Award, Users, Target, Zap, Globe, TrendingUp, ArrowRight, FileCheck, Lock, Scale, ChevronLeft, ChevronRight, HelpCircle, Sparkles } from 'lucide-react';
import { testimonials } from '../mockData';
import { toast } from 'sonner';
import PricingSection from '../components/PricingSection';
import UniversalPopupForm from '../components/UniversalPopupForm';
import SEO from '../components/SEO';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Auto-slide features (Federal Protection section)
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 3); // 3 features total
    }, 4000); // Change every 4 seconds
    return () => clearInterval(featureInterval);
  }, []);

  // Auto-slide 3-step process
  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 3); // 3 steps total
    }, 4000); // Change every 4 seconds
    return () => clearInterval(stepInterval);
  }, []);

  // Auto-slide testimonials (3 at a time)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => {
        const next = prev + 3;
        return next >= testimonials.length ? 0 : next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await submitLead(formData);
      
      if (result.success) {
        toast.success(result.message);
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = () => {
    if (window.zE) {
      window.openZendeskChat && window.openZendeskChat();
    } else {
      toast.info('Chat is loading... Please try again in a moment.');
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      const next = prev + 3;
      return next >= testimonials.length ? 0 : next;
    });
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => {
      const next = prev - 3;
      return next < 0 ? Math.max(0, testimonials.length - 3) : next;
    });
  };

  const faqs = [
    {
      question: "How long does the trademark registration process take in Canada?",
      answer: "The complete CIPO trademark registration process typically takes 12-18 months from filing to final approval. However, we submit your application promptly to get the process started immediately. The timeline depends on CIPO's examination schedule and whether any office actions are required."
    },
    {
      question: "What's included in your trademark registration service?",
      answer: "Our service includes a comprehensive trademark search, professional application preparation, CIPO filing, classification selection, response to office actions, and ongoing status monitoring until your trademark is registered. We handle all communications with CIPO on your behalf."
    },
    {
      question: "Do I need a trademark if I already have a business name?",
      answer: "Yes! A business name registration only reserves your name in your province for business purposes. A trademark gives you exclusive nationwide rights to use your brand name, logo, or slogan in commerce and prevents others from using it across all of Canada."
    },
    {
      question: "What if my trademark application is rejected?",
      answer: "With our 98% success rate, rejections are rare. However, if your application faces objections, we respond to all office actions and work with CIPO examiners to resolve issues. Our expert team knows how to navigate CIPO requirements to maximize your approval chances."
    },
    {
      question: "Can I trademark a logo and business name together?",
      answer: "Yes! You can file a combined trademark application that protects both your logo and business name together, or file separate applications for each. We'll recommend the best strategy based on your specific branding needs and budget."
    },
    {
      question: "How much does trademark registration cost?",
      answer: "Our service fees vary by package, starting with basic filing services to comprehensive protection packages. Government CIPO fees are separate and set by the Canadian Intellectual Property Office. Contact us for detailed pricing tailored to your needs."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Professional Canadian Trademark Registration"
        description="Expert CIPO trademark registration. 99% success rate. Federal trademark protection for Canadian businesses with specialists."
        keywords="trademark registration Canada, CIPO filing services, professional brand protection, Canadian trademark attorneys, federal trademark registration"
      />
      
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_brand-facelift/artifacts/27cpuetb_logo-color.png" 
                alt="Trademark Filing Canada" 
                className="h-10 w-auto"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsPopupOpen(true)}
                className="hidden sm:inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Register Your Trademark
              </button>
              <button
                onClick={handleChatClick}
                className="inline-flex items-center px-6 py-2.5 border-2 border-brand-600 text-brand-600 font-semibold rounded-lg hover:bg-brand-50 transition-all duration-200"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Redesigned for Landing Page */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Canada Flag Background */}
        <div className="absolute inset-0">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/d/d9/Flag_of_Canada_%28Pantone%29.svg"
            alt="Canada Flag"
            className="w-full h-full object-cover brightness-110"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/55 to-black/65"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Enhanced Content (Order 2 on mobile, 1 on desktop) */}
            <div className="text-white order-2 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-5 py-2.5 mb-8">
                <Sparkles className="w-4 h-4 text-brand-300" />
                <span className="text-white font-bold text-sm">Professional Canadian Trademark Services</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                Federal Trademark Registration
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 to-brand-400">
                  for Canadian Businesses
                </span>
              </h1>
              
              <p className="text-base text-brand-100 mb-6 leading-relaxed">
                Professional CIPO trademark services. Expert guidance through federal registration procedures ensuring comprehensive intellectual property protection.
              </p>

              {/* Trust Indicators - Redesigned */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Award className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">99%</div>
                  </div>
                  <div className="text-xs text-brand-200">Success Rate</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Users className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">1000+</div>
                  </div>
                  <div className="text-xs text-brand-200">Registrations</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Zap className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">15+</div>
                  </div>
                  <div className="text-xs text-brand-200">Years Experience</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="w-5 h-5 text-brand-300" />
                    <div className="text-2xl font-black">Federal</div>
                  </div>
                  <div className="text-xs text-brand-200">CIPO Filing</div>
                </div>
              </div>
            </div>

            {/* Right Side - Redesigned Form (Order 1 on mobile, 2 on desktop) */}
            <div className="relative order-1 lg:order-2">
              {/* Decorative Elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-3xl opacity-20 blur-2xl"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 border-4 border-brand-400/30">
                {/* Form Header - CTA Oriented */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-brand-600 to-brand-600 rounded-2xl mb-3 shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl font-black text-gray-900 mb-1">
                    Initiate Trademark Application
                  </h2>
                  <p className="text-gray-600 text-xs font-semibold">
                    Professional Federal Registration Services
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                        className="px-2 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-bold text-xs"
                      >
                        <option value="+1">+1 Canada</option>
                        <option value="+1">🇺🇸 +1 USA</option>
                        <option value="+44">🇬🇧 +44 UK</option>
                        <option value="+91">🇮🇳 +91 India</option>
                        <option value="+86">🇨🇳 +86 China</option>
                        <option value="+81">🇯🇵 +81 Japan</option>
                        <option value="+82">🇰🇷 +82 S. Korea</option>
                        <option value="+61">🇦🇺 +61 Australia</option>
                        <option value="+49">🇩🇪 +49 Germany</option>
                        <option value="+33">🇫🇷 +33 France</option>
                        <option value="+39">🇮🇹 +39 Italy</option>
                        <option value="+34">🇪🇸 +34 Spain</option>
                        <option value="+7">🇷🇺 +7 Russia</option>
                        <option value="+55">🇧🇷 +55 Brazil</option>
                        <option value="+52">🇲🇽 +52 Mexico</option>
                        <option value="+27">🇿🇦 +27 S. Africa</option>
                        <option value="+971">🇦🇪 +971 UAE</option>
                        <option value="+65">🇸🇬 +65 Singapore</option>
                        <option value="+31">🇳🇱 +31 Netherlands</option>
                        <option value="+46">🇸🇪 +46 Sweden</option>
                      </select>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 15) {
                              setFormData({ ...formData, phone: value });
                            }
                          }}
                          className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none text-gray-900 font-medium text-sm"
                          placeholder="4165551234"
                          minLength="10"
                          maxLength="15"
                          pattern="[0-9]{10,15}"
                          title="Please enter a valid phone number (10-15 digits)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Your Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows="2"
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none resize-none text-gray-900 text-sm"
                        placeholder="Tell us about your trademark..."
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800 text-white font-black py-3 px-6 rounded-xl hover:from-brand-700 hover:via-brand-800 hover:to-brand-900 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 shadow-xl hover:shadow-2xl text-sm"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Shield className="w-5 h-5" />
                        <span>Request Consultation →</span>
                      </div>
                    )}
                  </button>

                  {/* Trust Badge */}
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 mb-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span>🔒 Professional Consultation • No Obligation</span>
                    </div>
                    <p className="text-xs text-brand-600 font-bold">
                      ⚡ Join 1000+ Successful Registrations
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 1 - Why Trademark Protection Matters */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984"
                alt="Business strategy and brand protection"
                className="relative rounded-2xl shadow-2xl w-full h-[350px] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 max-w-xs">
                <div className="flex items-center space-x-3 mb-1">
                  <div className="bg-brand-100 rounded-full p-2">
                    <Shield className="w-5 h-5 text-brand-600" />
                  </div>
                  <span className="font-bold text-gray-900">Brand Protection</span>
                </div>
                <p className="text-sm text-gray-600">Secure your business identity nationwide</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-3 text-sm font-semibold">
                Strategic Protection
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Federal Trademark Rights Protect Your Business
              </h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                Federal trademark registration through CIPO establishes exclusive nationwide rights. Without proper registration, competitors may adopt similar marks, causing market confusion and brand dilution.
              </p>

              {/* Auto-sliding Features for Mobile */}
              <div className="block md:hidden relative">
                <div className="overflow-hidden">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentFeature * 100}%)` }}
                  >
                    {/* Feature 1 */}
                    <div className="w-full flex-shrink-0 px-2">
                      <div className="flex items-start space-x-3 bg-white p-4 rounded-xl shadow-md">
                        <div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
                          <Lock className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-sm">Federal Protection</h3>
                          <p className="text-gray-600 text-sm">Establish exclusive legal rights to marks across all Canadian jurisdictions.</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="w-full flex-shrink-0 px-2">
                      <div className="flex items-start space-x-3 bg-white p-4 rounded-xl shadow-md">
                        <div className="bg-green-100 rounded-lg p-2 flex-shrink-0">
                          <Scale className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-sm">Legal Standing</h3>
                          <p className="text-gray-600 text-sm">Enforcement rights for infringement proceedings under Canadian law.</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="w-full flex-shrink-0 px-2">
                      <div className="flex items-start space-x-3 bg-white p-4 rounded-xl shadow-md">
                        <div className="bg-brand-100 rounded-lg p-2 flex-shrink-0">
                          <TrendingUp className="w-5 h-5 text-brand-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-sm">Asset Value</h3>
                          <p className="text-gray-600 text-sm">Registered trademarks constitute transferable intellectual property assets.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dots Indicator */}
                <div className="flex justify-center mt-4 space-x-2">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentFeature(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentFeature === index ? 'bg-brand-600 w-8' : 'bg-gray-300'
                      }`}
                      aria-label={`Go to feature ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Desktop - Stack Vertically (No Slider) */}
              <div className="hidden md:block space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
                    <Lock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">Federal Protection</h3>
                    <p className="text-gray-600 text-sm">Establish exclusive legal rights to marks across all Canadian jurisdictions.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 rounded-lg p-2 flex-shrink-0">
                    <Scale className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">Legal Standing</h3>
                    <p className="text-gray-600 text-sm">Enforcement rights for infringement proceedings under Canadian law.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-brand-100 rounded-lg p-2 flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">Asset Value</h3>
                    <p className="text-gray-600 text-sm">Registered trademarks constitute transferable intellectual property assets.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 2 - CIPO Expertise - Redesigned */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 rounded-full px-6 py-3 mb-6 text-sm font-bold">
              CIPO Expertise
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Professional CIPO Registration
              <br />
              <span className="text-brand-600">Services</span>
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Canadian Intellectual Property Office registration requires comprehensive knowledge of federal examination standards. Our specialists provide expert guidance throughout the application process.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 mb-16">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-3xl p-6 md:p-8 border-2 border-red-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-red-600 mb-2 md:mb-3">42%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm">Independent Application Refusals</div>
              <p className="text-sm text-gray-600">Self-filed applications face higher rejection rates</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-white rounded-3xl p-6 md:p-8 border-2 border-green-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-green-600 mb-2 md:mb-3">99%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm">Professional Success Rate</div>
              <p className="text-sm text-gray-600">Expert preparation optimizes approval probability</p>
            </div>
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-3xl p-6 md:p-8 border-2 border-brand-100 text-center min-w-full md:min-w-0 snap-center flex-shrink-0">
              <div className="text-4xl md:text-5xl font-black text-brand-600 mb-2 md:mb-3">100%</div>
              <div className="text-gray-700 font-bold mb-2 text-sm">CIPO Compliance</div>
              <p className="text-sm text-gray-600">Full adherence to all federal requirements</p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 min-w-full md:min-w-0 snap-center flex-shrink-0 cursor-pointer group">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4 group-hover:bg-brand-600 transition-colors duration-300">
                <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-xl group-hover:text-brand-600 transition-colors duration-300">Strategic Classification</h3>
              <p className="text-gray-600 text-base break-words overflow-wrap-anywhere">Expert class selection for full brand protection</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-green-100 hover:border-green-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 min-w-full md:min-w-0 snap-center flex-shrink-0 cursor-pointer group">
              <div className="bg-green-100 rounded-xl p-3 w-fit mb-3 md:mb-4 group-hover:bg-green-600 transition-colors duration-300">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-xl group-hover:text-green-600 transition-colors duration-300">Clearance Search</h3>
              <p className="text-gray-600 text-base break-words overflow-wrap-anywhere">CIPO database analysis for conflict identification</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 min-w-full md:min-w-0 snap-center flex-shrink-0 cursor-pointer group">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4 group-hover:bg-brand-600 transition-colors duration-300">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-xl group-hover:text-brand-600 transition-colors duration-300">Case Management</h3>
              <p className="text-gray-600 text-base break-words overflow-wrap-anywhere">Dedicated coordination throughout the process</p>
            </div>

            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border-2 border-brand-100 hover:border-brand-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 min-w-full md:min-w-0 snap-center flex-shrink-0 cursor-pointer group">
              <div className="bg-brand-100 rounded-xl p-3 w-fit mb-3 md:mb-4 group-hover:bg-brand-600 transition-colors duration-300">
                <MessageSquare className="w-6 h-6 md:w-8 md:h-8 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-xl group-hover:text-brand-600 transition-colors duration-300">Examiner Response</h3>
              <p className="text-gray-600 text-base break-words overflow-wrap-anywhere">Professional office action handling</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="inline-flex items-center justify-center bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Register My Trademark
            </button>
            <button
              onClick={handleChatClick}
              className="inline-flex items-center justify-center bg-white text-brand-600 font-semibold px-8 py-4 rounded-lg border-2 border-brand-600 hover:bg-brand-50 hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Chat Now
            </button>
          </div>
        </div>
      </section>

      {/* 3-Step Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
              Simple Process
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Trademark in 3 Easy Steps
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              From consultation to registration, we handle everything to protect your brand
            </p>
          </div>

          {/* Mobile - Auto Slider */}
          <div className="block md:hidden pt-8">
            <div className="overflow-hidden relative">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentStep * 100}%)` }}
              >
                {/* Step 1 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border-2 border-brand-100 mt-6">
                    <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                      1
                    </div>
                    <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                      <FileCheck className="w-8 h-8 text-brand-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Consultation & Search
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed mb-4">
                      We review your trademark needs and conduct a comprehensive CIPO database search to ensure availability and registrability.
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Free initial consultation</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Comprehensive search report</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Strategic recommendations</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border-2 border-brand-100 mt-6">
                    <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                      2
                    </div>
                    <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                      <Target className="w-8 h-8 text-brand-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Application & Filing
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed mb-4">
                      Our experts prepare and file your complete trademark application with CIPO, ensuring all requirements are met for smooth processing.
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Professional application prep</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Proper class selection</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Direct CIPO filing</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="w-full flex-shrink-0 px-4">
                  <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border-2 border-brand-100 mt-6">
                    <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                      3
                    </div>
                    <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                      <Award className="w-8 h-8 text-brand-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Monitoring & Registration
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed mb-4">
                      We monitor your application, respond to any office actions, and guide you through to successful trademark registration.
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Application tracking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Office action responses</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Certificate delivery</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-8 space-x-2">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentStep === index ? 'bg-brand-600 w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop - Grid with Connection Lines */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 relative">
            {/* Connection Lines - Hidden on mobile */}
            <div className="absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-brand-200 via-brand-300 to-brand-200" style={{width: 'calc(100% - 200px)', left: '100px', zIndex: 0}}></div>

            {/* Step 1 */}
            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-brand-100 z-10">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                1
              </div>
              <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                <FileCheck className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Consultation & Search
              </h3>
              <p className="text-gray-600 text-center leading-relaxed mb-4">
                We review your trademark needs and conduct a comprehensive CIPO database search to ensure availability and registrability.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Free initial consultation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Comprehensive search report</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Strategic recommendations</span>
                </li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-brand-100 z-10">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                2
              </div>
              <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                <Target className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Application & Filing
              </h3>
              <p className="text-gray-600 text-center leading-relaxed mb-4">
                Our experts prepare and file your complete trademark application with CIPO, ensuring all requirements are met for smooth processing.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Professional application prep</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Proper class selection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Direct CIPO filing</span>
                </li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-brand-100 z-10">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl">
                3
              </div>
              <div className="mt-8 mb-6 bg-brand-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto">
                <Award className="w-8 h-8 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Monitoring & Registration
              </h3>
              <p className="text-gray-600 text-center leading-relaxed mb-4">
                We monitor your application, respond to any office actions, and guide you through to successful trademark registration.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Application tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Office action responses</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-brand-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Certificate delivery</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-16">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-10 py-4 rounded-xl hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-300 shadow-2xl inline-flex items-center"
            >
              <span>Get Started Now</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <p className="text-gray-600 mt-4 text-sm">No upfront payment required • Free consultation</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Testimonials - Sliding Carousel */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join hundreds of satisfied Canadian businesses
            </p>
          </div>

          <div className="relative">
            {/* Mobile View - Single Testimonial with Navigation */}
            <div className="md:hidden">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex mb-3">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-brand-400 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-base text-gray-700 italic mb-4 leading-relaxed">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div>
                  <p className="font-bold text-gray-900">
                    {testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-brand-600 text-sm font-medium">
                    {testimonials[currentTestimonial].business}
                  </p>
                  <p className="text-xs text-gray-500">
                    {testimonials[currentTestimonial].location}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Grid - 3 Testimonials at a Time */}
            <div className="hidden md:block">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.slice(currentTestimonial, currentTestimonial + 3).map((testimonial, index) => (
                  <div key={index} className="bg-gradient-to-br from-brand-50 to-white rounded-3xl p-8 border-2 border-brand-100 hover:border-brand-600 transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-brand-400 fill-brand-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-600 to-brand-800 rounded-full flex items-center justify-center text-white font-bold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.location}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dots Indicator - Desktop Only */}
              <div className="flex justify-center mt-8 space-x-2">
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index * 3)}
                    className={`transition-all duration-300 rounded-full ${
                      Math.floor(currentTestimonial / 3) === index ? 'bg-brand-600 w-8 h-3' : 'bg-gray-300 w-3 h-3'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Service Page Style */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about Canadian trademark registration
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e"
                alt="Professional consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our trademark experts are here to help answer all your CIPO registration questions.</p>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-2 px-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            {/* Right - FAQs */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Creative Final CTA */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b"
            alt="Halifax waterfront cityscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-brand-500/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Animated Badge */}
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by 500+ Canadian Businesses</span>
            </div>
          </div>

          {/* Main Heading */}
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            Don't Let Someone Else
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-400 to-brand-500">
              Steal Your Brand Name
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Every day you wait is another opportunity for competitors to register your brand. Protect your business name with CIPO registration today.
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">98%</div>
              <div className="text-brand-200 text-sm">Approval Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">500+</div>
              <div className="text-brand-200 text-sm">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-black text-white mb-2">24h</div>
              <div className="text-brand-200 text-sm">Fast Filing</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group inline-flex items-center space-x-3 bg-white text-brand-600 font-black px-10 py-5 rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-200 text-lg"
            >
              <Shield className="w-6 h-6" />
              <span>Protect Your Brand</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleChatClick}
              className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md text-white font-bold px-10 py-5 rounded-full border-2 border-white/30 hover:bg-white/20 transition-all duration-200 text-lg"
            >
              <MessageSquare className="w-6 h-6" />
              <span>Chat with Expert</span>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-white/80">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">Free Consultation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">No Hidden Fees</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">CIPO Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <span className="text-sm font-medium">Expert Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Universal Popup Form */}
      <UniversalPopupForm 
        isOpen={isPopupOpen} 
        onClose={() => setIsPopupOpen(false)}
        title="Protect Your Brand Today"
      />

      {/* Simple Footer - /lp specific */}
      <footer className="relative bg-gray-900 text-white py-8 z-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © 2026 Trademark Filing Canada. All rights reserved.
              </p>
              <p className="text-gray-400 text-sm mt-1 flex items-center justify-center md:justify-start">
                <Phone className="w-4 h-4 mr-2" />
                <a href="tel:+18885270051" className="hover:text-white transition-colors">+1 (888) 527-0051</a>
              </p>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
